// ============================================================
// src/pages/DealFormPublic.tsx
// Page publique (pas de ProtectedRoute) — le vendeur remplit le
// formulaire de deal (10 étapes, sans upload de documents) sans
// avoir besoin de compte. Les données sont écrites dans
// dealFormDrafts/{token}, jamais dans dealSubmissions directement.
// Le broker récupère ensuite ce brouillon depuis son dashboard.
// ============================================================
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useLanguage } from '@/contexts/LanguageContext';
import { SubmitDealForm } from '@/components/submit-deal/SubmitDealForm';
import { Loader2, AlertCircle, Building2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

interface DraftMeta {
  token: string;
  brokerId: string;
  repId: string;
  sellerName: string;
  sellerEmail: string;
  status: 'pending' | 'in_progress' | 'submitted';
  revoked: boolean;
}

export default function DealFormPublic() {
  const { token } = useParams<{ token: string }>();
  const { language } = useLanguage();
  const [draft, setDraft] = useState<DraftMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<'not_found' | 'revoked' | 'already_submitted' | null>(null);
  const fr = language === 'fr';

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const ref = doc(db, 'dealFormDrafts', token);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          setError('not_found');
          setLoading(false);
          return;
        }
        const data = snap.data() as DraftMeta;
        if (data.revoked) {
          setError('revoked');
          setLoading(false);
          return;
        }
        if (data.status === 'submitted') {
          setError('already_submitted');
          setLoading(false);
          return;
        }
        setDraft(data);

        // Marque le lien comme visité (pending -> in_progress) dès la
        // première ouverture, avant même que le vendeur ait tapé quoi
        // que ce soit — utile pour que le broker voie "vu" plutôt que
        // "envoyé, aucune activité" sur sa carte.
        if (data.status === 'pending') {
          await setDoc(ref, { status: 'in_progress', updatedAt: serverTimestamp() }, { merge: true });
        }
      } catch (err) {
        console.error('[DealFormPublic] Failed to load draft', err);
        setError('not_found');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <Loader2 className="w-8 h-8 animate-spin text-[#1D314D]" />
      </div>
    );
  }

  if (error || !draft) {
    const messages = {
      not_found: {
        title: fr ? 'Lien introuvable' : 'Link not found',
        body: fr
          ? "Ce lien n'existe pas ou a expiré. Contactez votre courtier pour obtenir un nouveau lien."
          : 'This link does not exist or has expired. Contact your broker for a new link.',
      },
      revoked: {
        title: fr ? 'Lien désactivé' : 'Link deactivated',
        body: fr
          ? 'Ce lien a été désactivé par votre courtier. Contactez-le pour obtenir un nouveau lien.'
          : 'This link has been deactivated by your broker. Contact them for a new link.',
      },
      already_submitted: {
        title: fr ? 'Formulaire déjà soumis' : 'Form already submitted',
        body: fr
          ? 'Vous avez déjà soumis ce formulaire à votre courtier. Aucune autre action requise de votre part pour le moment.'
          : "You've already submitted this form to your broker. No further action is needed from you at this time.",
      },
    };
    const m = messages[error || 'not_found'];
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center space-y-3">
            <AlertCircle className="w-10 h-10 text-muted-foreground mx-auto" />
            <h1 className="text-xl font-semibold text-[#1D314D]">{m.title}</h1>
            <p className="text-sm text-muted-foreground">{m.body}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="bg-[#1D314D] py-4 px-6 text-center">
        <div className="flex items-center justify-center gap-2 text-white">
          <Building2 className="w-5 h-5 text-[#CEAB65]" />
          <span className="font-semibold">AcquizGroup™</span>
        </div>
        <p className="text-xs text-white/70 mt-1">
          {fr
            ? `Formulaire de vente d'entreprise — ${draft.sellerName}`
            : `Business Sale Form — ${draft.sellerName}`}
        </p>
      </div>
      <div className="py-6">
        <SubmitDealForm
          publicDraftToken={token}
          publicDraftMeta={{ brokerId: draft.brokerId, repId: draft.repId }}
        />
      </div>
    </div>
  );
}